import { localGet } from "@/common/utils"
import { deleteCookie, getCookie } from "@/common/utils/cookie"
import { message } from "ant-design-vue"
import axios from "axios"
import JSONBig from "json-bigint"
import { getDateStr } from "./getDateStr"

// =============== 请求防重复处理 =================
const pendingRequests = new Map()

// 自动取消重复请求的接口白名单（只在这些接口启用防重复，避免误杀并发请求）
// 如需对某接口启用，往此数组添加 URL 关键字即可；
// 也可在单次请求 config 上手动设置 cancelDuplicate: true 来按需开启
const cancelWhiteList = [
	'/department/list',
]

function getRequestKey(config) {
	// 忽略查询参数，以方法 + 路径为唯一标识
	const url = config.url ? config.url.split("?")[0] : ""
	return `${config.method}:${url}`
}

function removePendingRequest(config) {
	if (!config.url) return
	const matchesWhiteList = cancelWhiteList.some((item) => config.url.includes(item))
	if (config.cancelDuplicate || matchesWhiteList) {
		const key = getRequestKey(config)
		if (pendingRequests.has(key)) {
			const controller = pendingRequests.get(key)
			controller.abort("canceled") // 中断上一个未完成的相同请求
			pendingRequests.delete(key)
		}
	}
}
// ================================================

axios.defaults.baseURL = "/"
// 超时时间（ms）
axios.defaults.timeout = 2000 * 1000
axios.defaults.withCredentials = true
axios.defaults.headers.post["Content-Type"] = "application/jsson"
axios.defaults.transformResponse = [
	(data) => {
		return JSONBig.parse(data)
	},
]
// 请求拦截
axios.interceptors.request.use(
	(config) => {
		const token = getCookie("d_session_id")
		const Authorization = getCookie("Authorization")
		config.headers["D-SESSION-ID"] = token
		config.headers.Authorization = Authorization
		// 可在这里做一些数据的校验。
		// path
		let path = config.url
		if (path.startsWith("null")) {
			path = path.replace("null", localStorage.getItem("apiURL"))
		}
		const requestId = guid()
		if (path.indexOf("?") > 0) {
			path = `${path}&requestId=${requestId}`
		} else {
			path = `${path}?requestId=${requestId}`
		}
		config.url = path

		// 尝试取消之前的重复请求
		removePendingRequest(config)

		// 为当前请求注册新的 AbortController
		const matchesWhiteList = cancelWhiteList.some((item) => config.url && config.url.includes(item))
		if (config.cancelDuplicate || matchesWhiteList) {
			const controller = new AbortController()
			config.signal = controller.signal
			config._abortController = controller
			pendingRequests.set(getRequestKey(config), controller)
		}

		// session的校验等。
		return config
	},
	(error) => {
		return error
	}
)
// 响应拦截
axios.interceptors.response.use(
	(result) => {
		// 请求成功后从池中移除，释放内存
		if (result?.config) {
			const matchesWhiteList = cancelWhiteList.some((item) => result.config.url && result.config.url.includes(item))
			if (result.config.cancelDuplicate || matchesWhiteList) {
				const key = getRequestKey(result.config)
				// 只有当 Map 里存的还是当前控制器时才删除，防止误删后发请求的控制器
				if (pendingRequests.get(key) === result.config._abortController) {
					pendingRequests.delete(key)
				}
			}
		}

		const data = result.data
		if (String(data.code) !== "1000000") {
			console.log(data)
			if (data.msg) {
				message.error(data.msg)
			}
			return Promise.reject(data.result)
		}
		return Promise.resolve(data.result)
	},
	(err) => {
		// 请求失败或被取消后从池中移除
		if (err?.config) {
			const matchesWhiteList = cancelWhiteList.some((item) => err.config.url && err.config.url.includes(item))
			if (err.config.cancelDuplicate || matchesWhiteList) {
				const key = getRequestKey(err.config)
				if (pendingRequests.get(key) === err.config._abortController) {
					pendingRequests.delete(key)
				}
			}
		}

		// 被主动取消的请求，静默处理，不弹报错
		if (axios.isCancel(err) || err.name === "CanceledError" || err.message === "canceled") {
			console.log("请求由于产生新请求而取消:", err)
			return Promise.reject(err)
		}

		console.log(JSON.stringify(err))
		const errList = ["Unexpected '<'", "Network Error"]
		const data = err?.response?.data
		if (!err.response && errList.includes(err.message)) {
			message.destroy()
			message.error("网络异常，请求超时")
			return Promise.reject(data)
		}
		if (!data?.config?.url.includes("logout") && data?.subCode === "sys.user-error-unlogin") {
			deleteCookie(() => {
				localStorage.clear()
				window.location.href = "/login"
			})
		}
		message.error(data?.msg)
		return Promise.reject(data)
	}
)

function guid() {
	const time = getDateStr(0, true).replace(/\D/g, "")
	const userAccount = localGet("userAccount") || "xxx"
	const s4 = () =>
		Math.floor((1 + Math.random()) * 0x10000)
			.toString(16)
			.substring(1)
	return `rqname${userAccount}${time}${s4()}`
}

export default axios
